import * as yup from 'yup';
import {
  EMAIL_REGEX,
  LINKEDIN_PROFILE_REGEX,
  PASSWORD_REGEX,
  PHONE_NO_REGEX,
  USERNAME_EMAIL_REGEX,
  USERNAME_REGEX,
} from '../constants/settings';

// function to get validation schema for select field declared here
const validationSchemaForSelectData = (testName: string, errorMsg: string) => {
  return yup
    .object()
    .shape({
      id: yup.number(),
      name: yup.string(),
    })
    .test(testName, errorMsg, (value) => value.id !== -1);
};

export const logInFormSchema = yup.object().shape({
  username_or_email: yup
    .string()
    .required('Please, enter your username or email!')
    .test('validate', 'Invalid username or email!', (value) => {
      return USERNAME_EMAIL_REGEX.test(value);
    }),
  password: yup.string().required('Please, enter your password!'),
});

// validation schema for signup form declared here
export const signupFormSchema = yup.object().shape({
  username: yup
    .string()
    .required('Please, provide an username!')
    .test(
      'validate',
      `Username must not contain any space or special characters except '_' or '-' and must be at least 3 characters long`,
      (value) => {
        return USERNAME_REGEX.test(value);
      }
    ),
  email: yup
    .string()
    .required('Please, provide your email!')
    .test('validate', 'Invalid email!', (value) => {
      return EMAIL_REGEX.test(value);
    }),
  password: yup
    .string()
    .required('Please, provide a password!')
    .matches(PASSWORD_REGEX, 'Invalid password!'),
  confirm_password: yup
    .string()
    .required('Please, reenter new password to confirm!')
    .oneOf([yup.ref('password')], 'Password mismatched!'),
  is_agree: yup
    .boolean()
    .oneOf([true], 'You must agree before signing up!')
    .required('You must agree before singing up!'),
});

// validation schema for change password form declared here
export const changePasswordFromSchema = yup.object().shape({
  is_user: yup.boolean(),
  current_password: yup.string().when('is_user', {
    is: true,
    then: () => yup.string().required('Please, enter your current password!'),
  }),
  new_password: yup
    .string()
    .required('Please, enter new password!')
    .matches(PASSWORD_REGEX, 'Invalid password!')
    .notOneOf(
      [yup.ref('current_password')],
      'New password cannot be the old one!'
    ),
  confirm_password: yup
    .string()
    .required('Please, reenter new password to confirm!')
    .oneOf([yup.ref('new_password')], 'Password mismatched!'),
});

// validation schema for appointment search form declared here
export const appointmentSearchSchema = yup.object().shape({
  date: yup.string().required('Please, select a date!'),
  from_time: yup.string(),
  to_time: yup.string(),
});

// validation schema for appointment slot reservation here
export const appointmentSlotSchema = yup.object().shape({
  want_reminder: yup.boolean(),
  reminder_time: yup.boolean().when('want_reminder', {
    is: true,
    then: () =>
      yup
        .number()
        .min(1, 'Reminder time must be greater or equal to one minute!')
        .max(120, 'Reminder time must be less or equal to 120 minute!')
        .required('Please, enter a reminder time!'),
  }),
});

export const createEditUserSchema = yup.object().shape({
  is_editing: yup.boolean(),
  username: yup.string().when('is_editing', {
    is: false,
    then: () =>
      yup
        .string()
        .required('Please, provide an username!')
        .test(
          'validate',
          `Username must not contain any space or special characters except '_' or '-' and must be at least 3 characters long`,
          (value) => {
            return USERNAME_REGEX.test(value);
          }
        ),
  }),
  first_name: yup.string().required('Please, provide a first name!'),
  middle_name: yup.string(),
  last_name: yup.string().required('Please, provide a last name!'),
  email: yup
    .string()
    .required('Please, provide an email!')
    .test('validate', 'Invalid email!', (value) => {
      return EMAIL_REGEX.test(value);
    }),
  role: validationSchemaForSelectData(
    'role-selected',
    'Please, select a role!'
  ),
  password: yup.string().when('is_editing', {
    is: false,
    then: () =>
      yup
        .string()
        .required('Please, provide a password!')
        .matches(PASSWORD_REGEX, 'Invalid password!'),
  }),
  confirm_password: yup.string().when('is_editing', {
    is: false,
    then: () =>
      yup
        .string()
        .required('Please, reenter new password to confirm!')
        .oneOf([yup.ref('password')], 'Password mismatched!'),
  }),
  province: validationSchemaForSelectData(
    'province-selected',
    'Please, select a province!'
  ),
  city: validationSchemaForSelectData(
    'city-selected',
    'Please, select a city!'
  ),
  postal_code: validationSchemaForSelectData(
    'postal-code-selected',
    'Please, select a postal code!'
  ),
  address: yup.string().required('Please, provide an address!'),
  isd_code: yup.string(),
  phone: yup
    .string()
    .required('Please, provide a phone number!')
    .test('validate', 'Invalid phone number!', function (value) {
      const { isd_code } = this.parent;
      console.log(isd_code);
      return PHONE_NO_REGEX.test(`${isd_code}${value}`);
    }),
  license_number: yup.string().required('Please, provide license number!'),
  business_reg_no: yup
    .string()
    .required('Please, provide business registration number!'),
  hst_number: yup.string().required('Please, provide a HST/TIN number!'),
  years_of_experience: yup
    .number()
    .required('Please, enter years of experience!')
    .min(1, 'Years of experience can not be negative or zero'),
  per_hour_rate: yup
    .number()
    .required('Please, enter expected hourly rate!')
    .min(1, 'Expected hourly rate can not be negative or zero'),
  linkedin_profile: yup
    .string()
    .test('validate', 'Invalid linkedin profile!', (value) => {
      if (value) {
        return LINKEDIN_PROFILE_REGEX.test(value);
      }

      return true;
    }),
});

export const createClientSchema = yup.object().shape({
  username: yup
    .string()
    .required('Please, provide an username!')
    .test(
      'validate',
      `Username must not contain any space or special characters except '_' or '-' and must be at least 3 characters long`,
      (value) => USERNAME_REGEX.test(value)
    ),
  email: yup
    .string()
    .required('Please, provide an email!')
    .test('validate', 'Invalid email!', (value) => EMAIL_REGEX.test(value)),
  password: yup
    .string()
    .required('Please, provide a password!')
    .matches(PASSWORD_REGEX, 'Invalid password!'),
  confirm_password: yup
    .string()
    .required('Please, reenter new password to confirm!')
    .oneOf([yup.ref('password')], 'Password mismatched!'),
});

export const editClientSchema = yup.object().shape({
  first_name: yup.string().required('Please, provide a first name!'),
  middle_name: yup.string(),
  last_name: yup.string().required('Please, provide a last name!'),
  email: yup
    .string()
    .required('Please, provide an email!')
    .test('validate', 'Invalid email!', (value) => EMAIL_REGEX.test(value)),
  address: yup.string().required('Please, provide an address!'),
  isd_code: yup.string(),
  phone: yup
    .string()
    .required('Please, provide a phone number!')
    .test('validate', 'Invalid phone number!', function (value) {
      const { isd_code } = this.parent;
      console.log(isd_code);
      return PHONE_NO_REGEX.test(`${isd_code}${value}`);
    }),
});

// validation schema for creating application declared here
export const createApplicationSchema = yup.object().shape({
  username: yup
    .string()
    .required('Please, enter a username!')
    .test(
      'validate',
      `Username must not contain any space or special characters except '_' or '-' and must be at least 3 characters long`,
      (value) => USERNAME_REGEX.test(value)
    ),
  email: yup
    .string()
    .required('Please, enter your email!')
    .test('validate', 'Invalid email!', (value) => EMAIL_REGEX.test(value)),

  password: yup.string().required('Please, provide your password!'),
  confirm_password: yup
    .string()
    .required('Please, reenter new password to confirm!')
    .oneOf([yup.ref('password')], 'Password mismatched!'),
  is_agree: yup
    .boolean()
    .oneOf([true], 'You must agree before submitting')
    .required('You must agree before submitting'),
});

export const saveProfileInfoSchema = yup.object().shape({
  first_name: yup.string().required('Please, enter your first name!'),
  middle_name: yup.string(),
  last_name: yup.string().required('Please, enter your last name!'),
  date_of_birth: yup.string(),
  linkedin_profile: yup
    .string()
    .test('validate', 'Invalid linkedin profile!', (value) => {
      if (value) {
        return LINKEDIN_PROFILE_REGEX.test(value);
      }

      return true;
    }),
  bio: yup.string(),
});

export const saveContactInfoSchema = yup.object().shape({
  isd_code: yup.string(),
  phone: yup
    .string()
    .required('Please, enter your phone number!')
    .test('validate', 'Invalid phone number!', function (value) {
      const { isd_code } = this.parent;
      return PHONE_NO_REGEX.test(`${isd_code}${value}`);
    }),
  address: yup.string().required('Please, enter your address!'),
});

export const saveProfessionalInfoSchema = yup.object().shape({
  license_number: yup.string().required('Please, provide license number!'),
  business_reg_no: yup
    .string()
    .required('Please, provide business registration number!'),
  hst_number: yup.string().required('Please, provide a HST/TIN number!'),
  years_of_experience: yup
    .number()
    .required('Please, enter your experience!')
    .min(1, 'Years of experience can not be negative or zero'),
  per_hour_rate: yup
    .number()
    .required('Please, enter your expected hourly rate!')
    .min(1, 'Expected hourly rate can not be negative or zero'),
  // is_eighteen: yup.bool().oneOf([true], 'This field is required!'),
});

// validation schema for update applicant status declared here
export const updateApplicationStatusSchema = yup.object().shape({
  comments: yup.string().required('Please, enter a comment!'),
});

// validation schema for updating profile declared here
export const updateProfileSchema = yup.object().shape({
  first_name: yup.string().required('Please, provide a first name!'),
  middle_name: yup.string(),
  last_name: yup.string().required('Please, provide a last name!'),
  email: yup
    .string()
    .required('Please, provide an email!')
    .test('validate', 'Invalid email!', (value) => {
      return EMAIL_REGEX.test(value);
    }),
  address: yup.string().required('Please, provide an address!'),
  isd_code: yup.string(),
  phone: yup
    .string()
    .required('Please, provide a phone number!')
    .test('validate', 'Invalid phone number!', function (value) {
      const { isd_code } = this.parent;
      console.log(isd_code);
      return PHONE_NO_REGEX.test(`${isd_code}${value}`);
    }),
  linkedin_profile: yup
    .string()
    .test('validate', 'Invalid linkedin profile!', (value) => {
      return value ? LINKEDIN_PROFILE_REGEX.test(value) : true;
    }),
});

// validation schema for conversion create/update declared here
export const createEditConversionSchema = yup.object().shape({
  name: yup.string().required('Please, enter the name of the conversion!'),
  minutes_per_credit: yup
    .string()
    .required('Please, enter number of minutes per credit!'),
  description: yup
    .string()
    .required('Please, enter short description of the conversion!'),
});

// validation schema for configuration create/update declared here
export const createEditConfigSchema = yup.object().shape({
  duration: yup
    .number()
    .required('Please, provide slot duration time!')
    .min(0, 'Slot duration can not be negative!'),
  buffer_time: yup
    .number()
    .required('Please, provide buffer time!')
    .min(0, 'Buffer time can not be negative!'),
  allow_rescheduling: yup.boolean(),
  reschedule_limit: yup.number().when('allow_rescheduling', {
    is: true,
    then: () =>
      yup
        .number()
        .required('Please, provide reschedule limit!')
        .min(0, 'Reschedule limit can not be less than zero!')
        .max(5, 'Reschedule limit can not be greater than five!'),
  }),
});

// validation schema for service create/update declared here
export const createEditServiceSchema = yup.object().shape({
  name: yup.string().required('Please, provide a service name!'),
  description: yup.string().required('Please, provide a service description!'),
});

// validation schema for rejecting vacation schema form declared here
export const rejectVacationRequestSchema = yup.object().shape({
  rejected_note: yup.string().required('Please, provide a comment!'),
});

// validation schema for rescheduling appointment form declared here
export const rescheduleAppointmentSchema = yup.object().shape({
  reschedule_reason: yup
    .string()
    .required('Please, enter a reason for rescheduling!'),
});

// validation schema for create/edit package form declared here
export const createEditPackageSchema = yup.object().shape({
  package_name: yup.string().required('Please, enter a package name!'),
  price: yup
    .number()
    .required('Please, enter a package price!')
    .min(1, 'Package price cannot be negative of zero!'),
  total_credits: yup
    .number()
    .required('Please, enter total credits!')
    .min(1, 'Total credits cannot be negative of zero!'),
});

export const createEditReferralSchema = yup.object().shape({
  reason: yup.string().required('Please, enter reason for your referral!'),
});

export const createEditTransactionSchema = yup.object().shape({
  transaction_id: yup.string().required('Please, enter the transaction id!'),
  paid_amount: yup
    .number()
    .required('Please, enter paid amount!')
    .min(1, 'Paid amount can not be less than one!'),
  receipt: yup
    .mixed<File>()
    .required('Please, upload payment receipt image!')
    .test('fileType', 'Unsupported file format!', (value) =>
      ['image/jpeg', 'image/png'].includes(value.type)
    ),
});

export const addEditProvinceSchema = yup.object().shape({
  gst: yup
    .number()
    .required('Please, enter GST rate!')
    .min(0, 'GST rate cannot be negative!')
    .max(100, 'GST rate cannot be greater than 100%!'),
  hst: yup
    .number()
    .required('Please, enter HST rate!')
    .min(0, 'HST rate cannot be negative!')
    .max(100, 'GST rate cannot be greater than 100%!'),
  pst: yup
    .number()
    .required('Please, enter PST rate!')
    .min(0, 'PST rate cannot be negative!')
    .max(100, 'GST rate cannot be greater than 100%!'),
  legal_age: yup
    .number()
    .required('Please, enter legal age value!')
    .min(13, 'Legal age must be greater than 12!'),
});

export const editCitySchema = yup.object().shape({
  city: yup.string().required('Please, enter city name!'),
});

export const addEditPostalCodeSchema = yup.object().shape({
  postal_code: yup
    .string()
    .test('min', 'Postal code must contain at least 5 characters!', (value) => {
      if (value) {
        return value.length >= 5;
      }

      return true;
    })
    .test(
      'max',
      'Postal code must not contain more than 10 characters!',
      (value) => {
        if (value) {
          return value.length <= 10;
        }

        return true;
      }
    ),
});

export const editCountrySchema = yup.object().shape({
  iso_code: yup.string().required('Please, enter ISO code!'),
  isd_code: yup.string().required('Please, enter ISD code!'),
});

export const createEditRetainAgreementSchema = yup.object().shape({
  is_editing: yup.boolean(),
  monthly_fee: yup
    .number()
    .min(1, 'Monthly fee cannot be less than one!')
    .required('Please, enter monthly fee!'),
  additional_hour_rate: yup
    .number()
    .min(1, 'Hourly rate cannot be less than one!')
    .required('Please, enter hourly rate!'),
  hours_included: yup
    .number()
    .min(1, 'Hours per week cannot be less than one!')
    .required('Please, enter hours per week!'),
  document: yup.mixed().when('is_editing', {
    is: false,
    then: () => yup.mixed().required('Please, upload agreement document!'),
  }),
});

export const clientFeedbackSchema = yup.object().shape({
  retain_answer: yup.string().required('Please, select an answer!'),
  rating: yup.number().required('Please, give a rating point!'),
  comments: yup.string().required('Please, provide your comments!'),
});

export const associateFeedbackSchema = yup.object().shape({
  comments: yup.string().required('Please, provide your comments!'),
});

export const mandatoryDocumentUploadSchema = yup.object().shape({
  document_file: yup.mixed().required('Please, upload the document file!'),
});

export const additionalDocumentUploadSchema = yup.object().shape({
  add_document_name: yup.string().required('Please, enter a document name!'),
  add_document_file: yup.mixed().required('Please, upload the document file!'),
});
